import type { DestinationContent } from 'components/DestinationEditor/DestinationEditorContext';
import { unionBy } from 'lodash';
import { useMemo } from 'react';
import type { DestinationContentTranslationSchema } from 'typings/destinations/DestinationContentTranslationSchema';

type UseDestinationTranslationProps = {
    content: DestinationContent;
    locale: string;
};

export type UpdateTranslationsValue = {
    contentId: number;
    translations: DestinationContentTranslationSchema[];
};

export function useDestinationContentTranslation({
    content,
    locale,
}: UseDestinationTranslationProps) {
    const isTranslation = locale !== 'en';
    const translatedContent = useMemo(() => {
        return locale === 'en'
            ? content
            : content.translations?.find(
                  (translation) => translation.language === locale,
              ) ?? {
                  title: '',
                  body: '',
              };
    }, [content, locale]);

    const handleUpdateTranslationField = (
        changes: Partial<DestinationContentTranslationSchema>,
    ) => {
        return mergeContentTranslationsByField(
            content.translations ?? [],
            [
                {
                    language: locale,
                    ...changes,
                },
            ],
            'language',
        );
    };

    return {
        translatedContent,
        isTranslation,
        handleUpdateTranslationField,
    };
}

export function mergeContentTranslationsByField(
    originalContents: DestinationContentTranslationSchema[],
    updatedContents: Partial<DestinationContentTranslationSchema>[],
    fieldToMerge: keyof DestinationContentTranslationSchema,
): DestinationContentTranslationSchema[] {
    // Filter out items that don't have the required field
    const validUpdatedContents = updatedContents.filter(
        (item) => item[fieldToMerge] !== undefined,
    );

    // Merge partial objects with original objects to preserve missing fields
    const mergedContents = validUpdatedContents.map((partialItem) => {
        const originalItem = originalContents.find(
            (original) => original[fieldToMerge] === partialItem[fieldToMerge],
        );
        return originalItem ? { ...originalItem, ...partialItem } : partialItem;
    }) as DestinationContentTranslationSchema[];

    // Use lodash unionBy with the merged array
    return unionBy(mergedContents, originalContents, fieldToMerge);
}
